import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpDown, LayoutGrid, List as ListIcon, MapPin } from "lucide-react";
import { fetchFarm, fetchThresholds } from "@/lib/api";
import {
    fdiLevel, fdiPosition, deltaTLevel, deltaTPosition,
    frostLevel, frostPosition, fmt, levelRank,
} from "@/lib/risk";
import { MiniRiskBar } from "@/components/RiskBar";
import StatusBadge from "@/components/StatusBadge";
import { cn } from "@/lib/utils";

const COLUMNS = [
    { key: "name", label: "Station" },
    { key: "fdi", label: "HFDI" },
    { key: "delta_t", label: "Delta T" },
    { key: "frost", label: "Frost" },
    { key: "rain_24h", label: "Rain 24h" },
    { key: "rain_7d", label: "Rain 7d" },
    { key: "max_min", label: "Max / Min" },
];

const sortValue = (s, key) => {
    switch (key) {
        case "name": return s.name;
        case "fdi": return s.current.fdi;
        case "delta_t": return s.current.delta_t;
        case "frost": return -s.forecast_min;
        case "rain_24h": return -s.rain_24h;
        case "rain_7d": return -s.rain_7d;
        case "max_min": return -s.max_temp;
        default: return 0;
    }
};

export default function StationsPage() {
    const { data } = useQuery({ queryKey: ["farm"], queryFn: fetchFarm, refetchInterval: 60_000 });
    const { data: thr } = useQuery({ queryKey: ["thresholds"], queryFn: fetchThresholds });
    const [sort, setSort] = useState({ key: "name", dir: "asc" });
    const [view, setView] = useState("list");

    const stations = useMemo(() => {
        if (!data?.stations) return [];
        const arr = [...data.stations];
        arr.sort((a, b) => {
            const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
            if (typeof va === "string") return sort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
            return sort.dir === "asc" ? va - vb : vb - va;
        });
        return arr;
    }, [data, sort]);

    if (!data || !thr) return <div className="h-40 bg-white rounded-2xl border animate-pulse" />;

    const toggleSort = (k) =>
        setSort((s) => (s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" }));

    return (
        <div className="space-y-5" data-testid="stations-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Weather Stations</h1>
                    <p className="text-sm text-slate-500 mt-1">All {stations.length} Davis Vantage Pro stations</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <ViewBtn active={view === "grid"} onClick={() => setView("grid")} icon={LayoutGrid} label="All Stations" testid="view-grid" />
                    <ViewBtn active={view === "list"} onClick={() => setView("list")} icon={ListIcon} label="List" testid="view-list" />
                    <Link to="/map" data-testid="view-map"
                        className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700">
                        <MapPin className="h-4 w-4" /> Map
                    </Link>
                </div>
            </div>

            {view === "list" ? (
                <>
                    {/* Desktop table */}
                    <div className="hidden md:block fw-card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                                    {COLUMNS.map((c) => (
                                        <th key={c.key} className="px-4 py-3">
                                            <button
                                                onClick={() => toggleSort(c.key)}
                                                data-testid={`sort-${c.key}`}
                                                className={cn("inline-flex items-center gap-1 hover:text-slate-800", sort.key === c.key && "text-slate-900")}
                                            >
                                                {c.label}
                                                <ArrowUpDown className="h-3 w-3" />
                                            </button>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {stations.map((s) => (
                                    <StationTableRow key={s.id} s={s} thr={thr} />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {stations.map((s) => <StationCard key={s.id} s={s} thr={thr} />)}
                    </div>
                </>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {stations.map((s) => <StationCard key={s.id} s={s} thr={thr} />)}
                </div>
            )}
        </div>
    );
}

function ViewBtn({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={cn(
                "inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg border transition-colors",
                active
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            )}
        >
            <Icon className="h-4 w-4" /> {label}
        </button>
    );
}

function StationTableRow({ s, thr }) {
    const fdiL = fdiLevel(s.current.fdi, thr.fdi);
    const dtL = deltaTLevel(s.current.delta_t, thr.delta_t);
    const frostL = frostLevel(s.forecast_min, thr.frost);
    return (
        <tr
            data-testid={`stations-row-${s.id}`}
            className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
            onClick={() => window.location.assign(`/stations/${s.id}`)}
        >
            <td className="px-4 py-3 font-semibold text-slate-900">
                <Link to={`/stations/${s.id}`} className="hover:underline">{s.name}</Link>
                <div className="text-[11px] text-slate-500 font-normal">{s.condition}</div>
            </td>
            <TdMetric value={fmt(s.current.fdi, 0)} level={fdiL} pos={fdiPosition(s.current.fdi, thr.fdi)} />
            <TdMetric value={`${fmt(s.current.delta_t, 1)}°`} level={dtL} pos={deltaTPosition(s.current.delta_t, thr.delta_t)} />
            <TdMetric value={`${fmt(s.forecast_min, 1)}°`} level={frostL} pos={frostPosition(s.forecast_min, thr.frost)} />
            <td className="px-4 py-3 fw-metric font-semibold text-slate-800">{fmt(s.rain_24h, 1)} mm</td>
            <td className="px-4 py-3 fw-metric font-semibold text-slate-800">{fmt(s.rain_7d, 1)} mm</td>
            <td className="px-4 py-3 fw-metric font-semibold text-slate-800">{fmt(s.max_temp, 1)} / {fmt(s.min_temp, 1)}°</td>
        </tr>
    );
}

function TdMetric({ value, level, pos }) {
    return (
        <td className="px-4 py-3">
            <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                    <span className="fw-metric font-bold text-slate-900">{value}</span>
                    <StatusBadge level={level} size="sm" />
                </div>
                <MiniRiskBar level={level} position={pos} />
            </div>
        </td>
    );
}

function StationCard({ s, thr }) {
    const fdiL = fdiLevel(s.current.fdi, thr.fdi);
    const dtL = deltaTLevel(s.current.delta_t, thr.delta_t);
    const frostL = frostLevel(s.forecast_min, thr.frost);
    return (
        <Link to={`/stations/${s.id}`} data-testid={`stations-card-${s.id}`} className="fw-card fw-card-hover p-5 block">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Station</div>
                    <div className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{s.name}</div>
                </div>
                <div className="text-right">
                    <div className="fw-metric text-2xl font-bold text-slate-900">{fmt(s.current.temp, 1)}°C</div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-wider">{s.condition}</div>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
                <CardMetric label="HFDI" value={fmt(s.current.fdi, 0)} level={fdiL} pos={fdiPosition(s.current.fdi, thr.fdi)} />
                <CardMetric label="Delta T" value={`${fmt(s.current.delta_t, 1)}°`} level={dtL} pos={deltaTPosition(s.current.delta_t, thr.delta_t)} />
                <CardMetric label="Frost min" value={`${fmt(s.forecast_min, 1)}°`} level={frostL} pos={frostPosition(s.forecast_min, thr.frost)} />
                <CardMetric label="Rain 24h" value={`${fmt(s.rain_24h, 1)} mm`} />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600">
                <div>Rain 7d: <span className="fw-metric font-semibold text-slate-800">{fmt(s.rain_7d, 1)} mm</span></div>
                <div>Max/Min: <span className="fw-metric font-semibold text-slate-800">{fmt(s.max_temp, 1)}/{fmt(s.min_temp, 1)}°</span></div>
            </div>
        </Link>
    );
}

function CardMetric({ label, value, level, pos }) {
    return (
        <div>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
                {level && <StatusBadge level={level} size="sm" />}
            </div>
            <div className="fw-metric font-bold text-slate-900 mt-0.5">{value}</div>
            {level && <div className="mt-1"><MiniRiskBar level={level} position={pos} /></div>}
        </div>
    );
}
